<template>
  <div class="cb-header__add" v-if="showAddMenu" ref="addMenu" @click.stop>
    <button
      type="button"
      class="cb-header__add-trigger"
      :class="{ 'cb-header__add-trigger--disabled': isAddMenuDisabled }"
      :style="themeColor"
      :disabled="isAddMenuDisabled"
      :aria-expanded="addMenuOpen ? 'true' : 'false'"
      :aria-disabled="isAddMenuDisabled ? 'true' : 'false'"
      aria-haspopup="true"
      @click="toggleAddMenu">
      {{ addLabel }}
      <i :class="addMenuOpen ? 'icon-angle-up' : 'icon-angle-down'"></i>
    </button>
    <ul v-if="addMenuOpen" class="cb-header__add-options" role="menu">
      <li
        v-if="canAddPlan"
        class="cb-header__add-option"
        role="menuitem"
        @click="selectAddOption(AddMenuOption.PLAN)">
        {{ addPlanLabel }}
      </li>
      <li
        v-if="canAddAddon"
        class="cb-header__add-option"
        role="menuitem"
        @click="selectAddOption(AddMenuOption.ADDON)">
        {{ addAddonLabel }}
      </li>
      <li
        v-if="canAddCoupon"
        class="cb-header__add-option"
        role="menuitem"
        @click="selectAddOption(AddMenuOption.COUPON)">
        {{ addCouponLabel }}
      </li>
    </ul>
  </div>
</template>
<script type="text/javascript">
import { EventBus } from "@/utils/event-bus"
import PortalHelper from "@/utils/portal-helper"
import { ItemType } from "@/utils/item-type-constants"

// JS enum for header Add-menu options (same pattern as paymentMethods / paymentStep).
const AddMenuOption = {
  PLAN: ItemType.PLAN,
  ADDON: ItemType.ADDON,
  COUPON: ItemType.COUPON,
};

export default {
  data() {
    return {
      addMenuOpen: false,
      AddMenuOption,
    }
  },
  computed: {
    isEditSubscriptionRoute() {
      return this.$route.name === "edit_subscription";
    },
    canAddPlan() {
      // Hide when MPS add-plan is off or no catalog plans remain.
      const subData = this.$store.getters.fetchSubData(this.$route.params.subscriptionId);
      return PortalHelper.canShowAddPlanOption({
        isMpsEnabled: this.$store.getters.is_mps_enabled,
        allowAddPlans: this.$store.getters.can_allow("change-subscription.action.allow_add_plans"),
        isItemsModel: !!(subData && subData.isItemsModel),
      }) && this.addablePlanItemPrices.length > 0;
    },
    addablePlanItemPrices() {
      const subData = this.$store.getters.fetchSubData(this.$route.params.subscriptionId);
      const cart = (this.$store.state.changeOrder
        && this.$store.state.changeOrder.cart
        && this.$store.state.changeOrder.cart.orderItems[0]) || {};
      return PortalHelper.getAddablePlanItemPrices({
        orderItem: cart,
        otherProductItems: subData && subData.other_product_items,
      });
    },
    canAddAddon() {
      const hasPermission = this.$store.getters.can_allow("change-subscription.action.allow_add_addons")
        || this.$store.getters.can_allow("change-subscription.action.allow_add_non_rec_addons");
      // Hide when no matching addons remain (same as body Add Addons).
      return hasPermission && this.addableAddonIds.length > 0;
    },
    addableAddonIds() {
      const subData = this.$store.getters.fetchSubData(this.$route.params.subscriptionId);
      const cart = (this.$store.state.changeOrder
        && this.$store.state.changeOrder.cart
        && this.$store.state.changeOrder.cart.orderItems[0]) || {};
      const otherProductItems = subData && subData.other_product_items;
      return PortalHelper.getAddableAddonIds({
        addonsMasterList: this.$store.getters.addons_master_list || [],
        cartAddons: cart.addons || [],
        isItemsModel: !!(subData && subData.isItemsModel),
        plan: this.$store.getters.change_order_plan,
        allItemPrices: (otherProductItems && otherProductItems.item_prices) || [],
        allowRecurringAddons: this.$store.getters.can_allow("change-subscription.action.allow_add_addons"),
        allowNonRecAddons: this.$store.getters.can_allow("change-subscription.action.allow_add_non_rec_addons"),
        orderItem: cart,
        otherProductItems,
        isMpsFamilyRestrictionEnabled: !!(
          this.$store.getters.is_mps_enabled
          && this.$store.getters.is_mps_family_restriction_enabled
        ),
      });
    },
    canAddCoupon() {
      return !!this.$store.getters.coupon_enabled
        && (this.$store.getters.multi_coupon_enabled ? true : !this.$store.getters.is_discount_present);
    },
    isEstimateLoaded() {
      return !!this.$store.getters.estimate_loaded;
    },
    // Visible during estimate load; block open until body handlers are ready.
    isAddMenuDisabled() {
      return !this.isEstimateLoaded;
    },
    isMpsEditUi() {
      return !!this.$store.getters.is_mps_enabled;
    },
    showAddMenu() {
      return this.isMpsEditUi
        && this.isEditSubscriptionRoute
        && (this.canAddPlan || this.canAddAddon || this.canAddCoupon);
    },
    addLabel() {
      return this.st("edit_subscription.add.label") || "Add";
    },
    addPlanLabel() {
      return this.st("edit_subscription.add_plan.label") || "Add plan";
    },
    addAddonLabel() {
      return this.t("add_addons.txt") || "Add add-on";
    },
    addCouponLabel() {
      return this.st("edit_subscription.add_coupon.label") || "Add coupon";
    }
  },
  watch: {
    showAddMenu(newValue) {
      if (!newValue) {
        this.addMenuOpen = false;
      }
    },
    isAddMenuDisabled(disabled) {
      if (disabled) {
        this.addMenuOpen = false;
      }
    },
    // Attach document click listener only while the menu is open.
    addMenuOpen(isOpen) {
      if (isOpen) {
        document.addEventListener("click", this.closeAddMenuOnOutsideClick);
      } else {
        document.removeEventListener("click", this.closeAddMenuOnOutsideClick);
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeAddMenuOnOutsideClick);
  },
  methods: {
    toggleAddMenu() {
      if (this.isAddMenuDisabled) {
        return;
      }
      this.addMenuOpen = !this.addMenuOpen;
    },
    closeAddMenuOnOutsideClick(event) {
      const container = this.$refs.addMenu;
      if (container && !container.contains(event.target)) {
        this.addMenuOpen = false;
      }
    },
    selectAddOption(type) {
      if (this.isAddMenuDisabled) {
        return;
      }
      this.addMenuOpen = false;
      if (type === AddMenuOption.ADDON) {
        EventBus.$emit("portal_header_add_addon");
      } else if (type === AddMenuOption.COUPON) {
        EventBus.$emit("portal_header_add_coupon");
      } else if (type === AddMenuOption.PLAN) {
        if (!this.canAddPlan) {
          return;
        }
        this.$router.customPush({ name: "add_plan" });
      }
    }
  }
}
</script>
<style lang="stylus" scoped>
  .cb-header{
    &__{
      &add{
        position: absolute;
        top: 50%;
        right: 36px;
        transform: translateY(-50%);
        text-align: right;
        z-index: 20;
        @media (max-width: 480px){
          right: 24px;
        }
      }
      &add-trigger{
        background: none;
        border: 0;
        padding: 4px 0;
        cursor: pointer;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        i{
          margin-left: 6px;
          font-size: 0.75em;
        }
        &--disabled, &[disabled]{
          opacity: 0.4;
          cursor: not-allowed;
        }
      }
      &add-options{
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        min-width: 180px;
        margin: 0;
        padding: 8px 0;
        list-style: none;
        background: #ffffff;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
        text-align: left;
      }
      &add-option{
        padding: 10px 20px;
        cursor: pointer;
        white-space: nowrap;
        &:hover{
          background: #f3f4f7;
        }
      }
    }
  }
</style>
